import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  buildDashboardDataTableColumns,
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
} from '@shigo-ui/dashboard-ui';
import { SegmentedControl, Switch } from '@shigo-ui/ui';
import { LoaderCircle } from 'lucide-react';

import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../lib/messages';
import { formatDisplayDate } from '../../../../lib/format';
import { showRequestError } from '../../../../lib/requestErrorToast';
import { formatShigoRole } from '../../../auth/roleLabels';
import {
  usePendingStitcherAccessUserIds,
  useSetStitcherAccessMutation,
  useStitcherAccessQuery,
} from '../hooks';
import { NetworkBadge } from './NetworkBadge';
import {
  StitcherAccessFilter,
  stitcherAccessPageSizes,
  type StitcherAccessFilterValue,
  type StitcherAccessUser,
} from '../model';
import {
  parseStitcherAccessRouteSearch,
  serializeStitcherAccessRouteSearch,
} from '../stitcherAccessRouteSearch';

export function StitcherAccessPage() {
  const navigate = useNavigate({ from: '/admin/stitcher-access' });
  const routeSearch = useSearch({ from: '/admin/stitcher-access' });
  const filter = parseStitcherAccessRouteSearch(routeSearch);
  const query = useStitcherAccessQuery(filter);
  const setUseStitcher = useSetStitcherAccessMutation();
  const pendingUserIds = usePendingStitcherAccessUserIds();

  const updateFilter = (next: StitcherAccessFilterValue) => {
    void navigate({ search: serializeStitcherAccessRouteSearch(next), replace: true });
  };
  const searchInput = useDebouncedValue(
    filter.searchTerm,
    (value) => {
      updateFilter({ ...filter, searchTerm: value, page: 0 });
    },
    650,
  );

  const accessOptions = [
    { value: StitcherAccessFilter.Granted, label: dashboardT('stitcherAccess.access.granted') },
    { value: StitcherAccessFilter.All, label: dashboardT('stitcherAccess.access.all') },
  ];
  const columns = buildDashboardDataTableColumns<StitcherAccessUser>([
    {
      id: 'name',
      header: dashboardT('stitcherAccess.columns.user'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (user) => (
        <div className="min-w-0">
          <div className="truncate">{user.name || user.username || user.email}</div>
          {user.email ? (
            <div className="text-muted-foreground truncate text-xs">{user.email}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'network',
      header: dashboardT('stitcherAccess.columns.network'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (user) =>
        user.networks.length ? (
          <div className="flex flex-wrap gap-1">
            {user.networks.map((network) => (
              <NetworkBadge
                key={network.id}
                name={network.name}
                roles={network.roles.map(formatShigoRole).join(', ') || '-'}
              />
            ))}
          </div>
        ) : null,
    },
    {
      id: 'roles',
      header: dashboardT('stitcherAccess.columns.role'),
      kind: DashboardDataTableColumnKind.Text,
      value: (user) => user.roles.map(formatShigoRole).join(', ') || null,
    },
    {
      id: 'enabled',
      header: dashboardT('stitcherAccess.columns.enabled'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (user) => {
        const pending = pendingUserIds.has(user.id);
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={user.useStitcher}
              disabled={pending}
              aria-busy={pending}
              aria-label={dashboardT('stitcherAccess.toggleFor', {
                user: user.name ?? user.username ?? user.email ?? user.id,
              })}
              onCheckedChange={(checked) => {
                setUseStitcher.mutate(
                  { userId: user.id, useStitcher: checked },
                  {
                    onError: (error) => {
                      showRequestError(error);
                    },
                  },
                );
              }}
            />
            {pending ? (
              <LoaderCircle
                className="text-muted-foreground h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'grantedOn',
      header: dashboardT('stitcherAccess.columns.grantedOn'),
      kind: DashboardDataTableColumnKind.Text,
      value: (user) => formatDisplayDate(user.stitcherGrantedOn ?? null),
    },
    {
      id: 'lastUsed',
      header: dashboardT('stitcherAccess.columns.lastUsed'),
      kind: DashboardDataTableColumnKind.Text,
      value: (user) => formatDisplayDate(user.stitcherLastUsedOn ?? null),
    },
  ]);

  return (
    <main className="page-gutter min-w-0 py-6">
      <DashboardDataTable
        title={dashboardT('stitcherAccess.title')}
        tableLabel={dashboardT('stitcherAccess.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(user) => user.id}
        loading={query.isFetching}
        loadingMessage={dashboardT('stitcherAccess.loading')}
        emptyMessage={dashboardT('stitcherAccess.empty')}
        errorMessage={query.isError ? dashboardT('stitcherAccess.loadError') : undefined}
        minWidthClassName="min-w-[960px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              value={searchInput.draft}
              aria-label={dashboardT('stitcherAccess.search')}
              placeholder={dashboardT('stitcherAccess.search')}
              onChange={(event) => {
                searchInput.setDraft(event.target.value);
              }}
            />
            <SegmentedControl
              ariaLabel={dashboardT('stitcherAccess.access.label')}
              value={filter.access}
              options={accessOptions}
              onValueChange={(access) => {
                updateFilter({ ...filter, access, page: 0 });
              }}
            />
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: stitcherAccessPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            updateFilter({ ...filter, page: next.page, size: next.pageSize });
          },
        }}
      />
    </main>
  );
}
